import { useTranslation } from "react-i18next";
import { MessageCircle, Info, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { LeadDialog } from "@/components/LeadDialog";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { CONTACT, waLink } from "@/data/site";

const BASE = `https://${CONTACT.domain}`;

const CommonHealthProblems = () => {
  const { t, i18n } = useTranslation();
  const sections = t("commonHealth.sections", { returnObjects: true }) || [];
  const lang = i18n.language;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("commonHealth.h1"),
    description: t("commonHealth.metaDescription"),
    author: { "@type": "Organization", name: CONTACT.brand },
    publisher: { "@type": "Organization", name: CONTACT.brand },
    url: `${BASE}/${lang}/common-health-problems-vietnam`,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: `${BASE}/${lang}` },
      { "@type": "ListItem", position: 2, name: t("nav.commonHealth"), item: `${BASE}/${lang}/common-health-problems-vietnam` },
    ],
  };

  return (
    <>
      <Seo
        title={t("commonHealth.seoTitle")}
        description={t("commonHealth.metaDescription")}
        path="/common-health-problems-vietnam"
        exact
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      <PageHero
        eyebrow={t("commonHealth.eyebrow")}
        title={t("commonHealth.h1")}
        subtitle={t("commonHealth.intro")}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.commonHealth") }]}
      />

      <section className="vc-container py-14 sm:py-16">
        <Reveal>
          <div className="mb-10 flex items-start gap-3 rounded-2xl border border-[hsl(var(--vc-line))] bg-mint p-5" data-testid="common-emergency-notice">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand text-white"><Info className="h-5 w-5" /></span>
            <p className="text-sm font-semibold leading-relaxed text-[hsl(var(--vc-ink))]">{t("commonHealth.emergency")}</p>
          </div>
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {sections.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.03}>
                  <article className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft" data-testid={`condition-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="grid h-10 w-10 place-items-center rounded-lg bg-mint text-brand"><HeartPulse className="h-5 w-5" /></span>
                      <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{s.title}</h2>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{s.text}</p>
                    <div className="mt-4 rounded-lg bg-mint px-4 py-3 text-sm">
                      <span className="font-semibold text-[hsl(var(--vc-ink))]">{t("commonHealth.outro")}</span>{" "}
                      <span className="text-[hsl(var(--vc-slate))]">{t("commonHealth.outroText")}</span>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-10"><EmergencyNotice /></Reveal>
          </div>

          <aside>
            <div className="lg:sticky lg:top-24 rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-card">
              <h3 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">{t("cta.getMedical")}</h3>
              <p className="mt-1.5 text-sm text-[hsl(var(--vc-slate))]">{t("commonHealth.outroText")}</p>
              <LeadDialog
                title={t("cta.getMedical")}
                defaultType="medical"
                source="common-health-problems"
                trigger={<Button className="mt-4 w-full bg-brand py-5 font-bold text-white hover:opacity-90" data-testid="common-request-btn">{t("cta.getMedical")}</Button>}
              />
              <Button asChild variant="outline" className="mt-3 w-full border-2 border-[hsl(var(--vc-line))] font-bold text-[hsl(var(--vc-ink))] hover:border-brand hover:bg-mint hover:text-brand">
                <a href={waLink("Hello Doctors Vietnam, I'm not feeling well and need help finding a doctor.")} target="_blank" rel="noopener noreferrer" data-testid="common-whatsapp-btn">
                  <MessageCircle className="mr-2 h-4 w-4" /> {t("cta.whatsappUs")}
                </a>
              </Button>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default CommonHealthProblems;
